import {HttpStatusCode} from '@angular/common/http';
import {Component} from '@angular/core';
import {FormBuilder, FormControl, Validators} from '@angular/forms';
import {AuthApiService, UserAttributesApiService} from '@npck/af-frontend-api';
import {BusinessErrorCode, RouterService, rxSpinner, toBusinessOrNull, TypedForm} from '@npck/libs/commons';
import {DialogService} from '@npck/libs/ui-kit';
import {SessionStorageService} from 'ngx-webstorage';
import {AuthHolderService} from '../../domain/auth-holder.service';
import {RETURN_URL} from '../../domain/browser-storage.keys';
import {DictionaryHolderService} from '../../domain/dictionary-holder.service';
import {ChangePasswordDialogComponent} from '../../layout/main-layout/change-password/change-password-dialog.component';
import {Path} from '../../path';

@Component({
    templateUrl: 'login.component.html',
    styleUrls: ['login.component.scss'],
    standalone: false
})
export class LoginComponent {
    form: TypedForm<LoginForm>;
    showPassword = false;
    notice: LoginNotice | null = null;

    constructor(
        private routerService: RouterService,
        private authHolderService: AuthHolderService,
        private sessionStorageService: SessionStorageService,
        private formBuilder: FormBuilder,
        private authApi: AuthApiService,
        private dictionaryHolderService: DictionaryHolderService,
        private userAttributesApi: UserAttributesApiService,
        private dialogService: DialogService,
    ) {
        this.buildForm();
    }

    login(): void {
        this.notice = null;
        const authorization = btoa(`${this.form.value.login}:${this.form.value.password}`);
        this.authApi.login(`Basic ${authorization}`, 'default', 'password')
            .pipe(rxSpinner('submit'))
            .subscribe({
                next: value => {
                    this.authHolderService.storeAuthToken(value);
                    this.authHolderService.loadUserAttributes(this.userAttributesApi);
                    this.dictionaryHolderService.preloadCommonDictionaries();
                    this.navigateAfterLogin();
                },
                error: err => {
                    const businessError = toBusinessOrNull(err)?.error;
                    if (businessError === BusinessErrorCode.AF_PASSWORD_CHANGE_REQUIRED) {
                        this.openActivationDialog();
                        return;
                    }
                    if (businessError === BusinessErrorCode.AF_PASSWORD_EXPIRED) {
                        this.notice = PASSWORD_EXPIRED_NOTICE;
                        return;
                    }
                    if (businessError === BusinessErrorCode.AF_USER_DISABLED) {
                        this.notice = USER_DISABLED_NOTICE;
                        return;
                    }
                    if (err.status === HttpStatusCode.Unauthorized) {
                        this.form.setErrors({wrongCredentials: true});
                        Object.keys(this.form.controls).forEach(key => {
                            this.form.get(key).setErrors({wrongCredentials: true});
                            this.form.get(key).markAsTouched();
                        });
                        this.listenToFormChanges();
                    } else {
                        throw err;
                    }
                },
            });
    }

    goToRecovery(): void {
        this.routerService.navigate(Path.PASSWORD_RECOVERY);
    }

    private openActivationDialog(): void {
        const activation = {username: this.form.value.login, otp: this.form.value.password};
        this.dialogService
            .showCustomDialog<ChangePasswordDialogComponent, boolean>(
                ChangePasswordDialogComponent,
                {activation},
                {disableClose: true, width: 'fit-content', panelClass: ['ui-kit-dialog-w-fit-content']},
            )
            .afterClosed()
            .subscribe(changed => {
                if (changed) {
                    this.form.reset();
                }
            });
    }

    private navigateAfterLogin(): void {
        const savedReturnUrl = this.sessionStorageService.retrieve(RETURN_URL);
        this.sessionStorageService.clear(RETURN_URL);
        const returnUrl = savedReturnUrl && JSON.parse(savedReturnUrl);
        let path = returnUrl?.path ?? Path.ANALYTICS;
        path = this.hasAccessToPath(path) ? path : Path.SUBJECTS;
        const params = returnUrl?.queryParams ? {queryParams: returnUrl.queryParams} : undefined;
        this.routerService.navigate(path, params);
    }

    private hasAccessToPath(path: string): boolean {
        if (path.startsWith(Path.SUBJECTS)) {
            return true;
        }
        return this.authHolderService.hasAccess(['npck-admin', 'finance-user']);
    }

    private listenToFormChanges(): void {
        const subscription = this.form.valueChanges.subscribe(() => {
            this.form.setErrors({wrongCredentials: null});
            Object.keys(this.form.controls).forEach(key => {
                this.form.get(key).setErrors({wrongCredentials: null});
                this.form.get(key).updateValueAndValidity({emitEvent: false});
            });
            subscription.unsubscribe();
        });
    }

    private buildForm(): void {
        this.form = this.formBuilder.group({
            login: new FormControl('', [Validators.required]),
            password: new FormControl('', [Validators.required]),
        });
    }
}

interface LoginForm {
    login: string;
    password: string
}

interface LoginNotice {
    accentKey: string;
    textKey: string;
}

const PASSWORD_EXPIRED_NOTICE: LoginNotice = {
    accentKey: 'i18n_af_errorPasswordExpiredAccent',
    textKey: 'i18n_af_errorPasswordExpired',
};

const USER_DISABLED_NOTICE: LoginNotice = {
    accentKey: 'i18n_af_errorUserDisabledAccent',
    textKey: 'i18n_af_errorUserDisabled',
};
